import { EnduserSession } from "@tellescope/sdk";
import { Enduser } from "@tellescope/types-models";
import { EnduserSliceCreator, EnduserAuthSlice, PersistedSessionData, LoginFlowResult, RegisterArgs } from "../types";
import { getApiURL } from "../../../config/api";
import { useOrganizationStore } from "../../../shared/store/organizationStore";
import { tokenRequiresMFA } from "../../../shared/utils/jwt";

export const createEnduserAuthSlice: EnduserSliceCreator<EnduserAuthSlice> = (set, get) => ({
  session: null,
  isLoading: false,
  currentEnduser: null,
  tokenError: null,

  enduserPasswordIsUnset: false,
  passwordPromptDismissed: false,
  loginFlowPasswordIsUnset: false,

  // OTP state (shared by recovery and MFA flows)
  otpToken: null,
  otpMethods: null,
  otpSentVia: null,
  otpError: null,
  otpMode: null,

  // Login flow state
  loginFlowEmail: null,
  loginFlowOtpToken: null,
  loginFlowError: null,

  initializeFromToken: async (token: string, businessId?: string) => {
    set({ isLoading: true, tokenError: null });

    try {
      // Create EnduserSession with the one-time token
      // businessId can be passed explicitly or will be populated by the SDK from the token
      const enduserSession = new EnduserSession({
        host: getApiURL(),
        authToken: token,
        businessId: businessId || "", // Will be populated by refresh_session from token claims
        handleUnauthenticated: async () => {
          get().clearAuth();
        },
      });

      // Immediately refresh to get a persistent token (one-time token pattern)
      // The SDK handles storing the refreshed token internally
      const refreshResult = await enduserSession.refresh_session({
        invalidatePreviousToken: true,
      });

      const finalSession = new EnduserSession({
        host: getApiURL(),
        authToken: refreshResult.authToken,
        businessId: refreshResult.enduser?.businessId || "",
        handleUnauthenticated: async () => {
          get().clearAuth();
        },
      });

      set({
        session: finalSession,
        currentEnduser: refreshResult.enduser,
        enduserPasswordIsUnset: false, // No password signal available from magic link path
        passwordPromptDismissed: false,
        isLoading: false,
        tokenError: null,
      });

      return true;
    } catch (error: unknown) {
      console.error("Enduser token authentication failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Invalid or expired access link";

      // Try OTP recovery - if methods are available, enter OTP flow instead of showing error
      const hasOtpRecovery = await get().initializeOtpRecovery(token);

      if (hasOtpRecovery) {
        // OTP recovery available - don't set tokenError, let UI show OTP form
        set({ isLoading: false });
      } else {
        // No OTP recovery available - show error page
        set({
          session: null,
          currentEnduser: null,
          isLoading: false,
          tokenError: errorMessage,
        });
      }

      return false;
    }
  },

  restoreSession: (data: PersistedSessionData) => {
    // Recreate session from persisted data (for page refresh restoration)
    const enduserSession = new EnduserSession({
      host: getApiURL(),
      authToken: data.authToken,
      businessId: data.businessId,
      handleUnauthenticated: async () => {
        get().clearAuth();
      },
    });

    set({
      session: enduserSession,
      currentEnduser: data.enduser,
      isLoading: false,
      tokenError: null,
    });
  },

  logout: async () => {
    const { session } = get();
    if (!session) return;

    try {
      await session.logout();
    } catch (error) {
      console.error("Enduser logout error:", error);
    } finally {
      get().clearAuth();
    }
  },

  clearAuth: () => {
    set({
      session: null,
      currentEnduser: null,
      isLoading: false,
      tokenError: null,
      enduserPasswordIsUnset: false,
      passwordPromptDismissed: false,
      // Also clear OTP state
      otpToken: null,
      otpMethods: null,
      otpSentVia: null,
      otpError: null,
      otpMode: null,
      // Also clear login flow state
      loginFlowEmail: null,
      loginFlowOtpToken: null,
      loginFlowError: null,
      loginFlowPasswordIsUnset: false,
    });
  },

  setTokenError: (error) => set({ tokenError: error }),

  updateCurrentEnduser: (updates) => {
    const currentEnduser = get().currentEnduser;
    if (currentEnduser) {
      set({ currentEnduser: { ...currentEnduser, ...updates } });
    }
  },

  refreshSession: async () => {
    const { session } = get();
    if (!session) return false;

    try {
      const refreshResult = await session.refresh_session({
        invalidatePreviousToken: false, // Don't invalidate - we just want a fresh token
      });

      session.setAuthToken(refreshResult.authToken);

      // Update current enduser if returned
      if (refreshResult.enduser) {
        set({ currentEnduser: refreshResult.enduser as Enduser });
      }

      return true;
    } catch (error) {
      console.error("Session refresh failed:", error);
      return false;
    }
  },

  // OTP Recovery Actions

  initializeOtpRecovery: async (token: string) => {
    try {
      // Create a temporary session to call get_otp_methods
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: token,
        businessId: "",
      });

      const result = await tempSession.api.endusers.get_otp_methods({ token });

      if (result.methods && result.methods.length > 0) {
        set({
          otpToken: token,
          otpMethods: result.methods,
          otpSentVia: null,
          otpError: null,
          otpMode: "recovery",
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error("Failed to initialize OTP recovery:", error);
      return false;
    }
  },

  sendOtpCode: async (method: string) => {
    const { otpToken } = get();
    if (!otpToken) return false;

    try {
      const organization = useOrganizationStore.getState().organization;
      const brandId = organization?.brandId;

      // Create a temporary session to call send_otp
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: otpToken,
        businessId: "",
      });

      await tempSession.api.endusers.send_otp({
        token: otpToken,
        method,
        ...(brandId && { brandId }),
      });
      set({ otpSentVia: method, otpError: null });
      return true;
    } catch (error) {
      console.error("Failed to send OTP:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send code";
      set({ otpError: errorMessage });
      return false;
    }
  },

  verifyOtpCode: async (code: string) => {
    const { otpToken } = get();
    if (!otpToken) return false;

    try {
      // Create a temporary session to call verify_otp
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: otpToken,
        businessId: "",
      });

      const result = await tempSession.api.endusers.verify_otp({ token: otpToken, code });

      // Create a new session with the verified token
      const newSession = new EnduserSession({
        host: getApiURL(),
        authToken: result.authToken,
        businessId: result.enduser?.businessId || "",
        handleUnauthenticated: async () => {
          get().clearAuth();
        },
      });

      set({
        session: newSession,
        currentEnduser: result.enduser,
        enduserPasswordIsUnset: false, // No password signal available from magic link OTP path
        passwordPromptDismissed: false,
        isLoading: false,
        tokenError: null,
        // Clear OTP state
        otpToken: null,
        otpMethods: null,
        otpSentVia: null,
        otpError: null,
        otpMode: null,
      });

      return true;
    } catch (error) {
      console.error("Failed to verify OTP:", error);
      const errorMessage = error instanceof Error ? error.message : "Invalid code";
      set({ otpError: errorMessage });
      return false;
    }
  },

  clearOtpState: () => {
    set({
      otpToken: null,
      otpMethods: null,
      otpSentVia: null,
      otpError: null,
      otpMode: null,
    });
  },

  initializeOtpForMfa: async (token: string) => {
    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";

      // Create a temporary session to get OTP methods
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: token,
        businessId,
      });

      const result = await tempSession.api.endusers.get_otp_methods({ token });

      if (result.methods && result.methods.length > 0) {
        set({
          otpToken: token,
          otpMethods: result.methods,
          otpSentVia: null,
          otpError: null,
          otpMode: "mfa",
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error("Failed to initialize MFA OTP:", error);
      return false;
    }
  },

  // Login Flow Actions

  setLoginFlowError: (error) => set({ loginFlowError: error }),

  clearLoginFlowState: () => {
    set({
      loginFlowEmail: null,
      loginFlowOtpToken: null,
      loginFlowError: null,
      loginFlowPasswordIsUnset: false,
      // Also clear OTP state used during login
      otpSentVia: null,
      otpError: null,
    });
  },

  beginLoginFlow: async (emailOrPhone: string, dateOfBirth?: string): Promise<LoginFlowResult | null> => {
    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";
      const brandId = organization?.brandId;

      // Create a temporary session for the login flow
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: "",
        businessId,
      });

      // Determine if input is email or phone
      const isEmail = emailOrPhone.includes("@");
      const loginArg = isEmail
        ? { email: emailOrPhone, ...(dateOfBirth && { dateOfBirth }), ...(brandId && { brandId }) }
        : { phone: emailOrPhone, ...(dateOfBirth && { dateOfBirth }), ...(brandId && { brandId }) };

      const result = await tempSession.begin_login_flow(loginArg);

      // Store the email/phone, token, and whether the enduser has no password
      // sent-email/sent-sms means backend sent OTP directly — no password exists
      set({
        loginFlowEmail: emailOrPhone,
        loginFlowOtpToken: result.otpToken || null,
        loginFlowPasswordIsUnset: result.result === "sent-email" || result.result === "sent-sms",
        isLoading: false,
      });

      return {
        result: result.result as LoginFlowResult["result"],
        email: result.email,
        otpToken: result.otpToken,
      };
    } catch (error) {
      console.error("Begin login flow failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Login failed";
      set({ loginFlowError: errorMessage, isLoading: false });
      return null;
    }
  },

  authenticateWithPassword: async (email: string, password: string): Promise<boolean> => {
    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";

      // Create a temporary session to authenticate
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: "",
        businessId,
      });

      const result = await tempSession.authenticate(email, password);

      // Check if token requires MFA verification
      if (tokenRequiresMFA(result.authToken)) {
        // Don't create session yet - need MFA verification first
        const mfaInitialized = await get().initializeOtpForMfa(result.authToken);
        set({ isLoading: false });
        // Return true if MFA flow was initialized - caller navigates to OTP page
        // Return false if MFA initialization failed (no OTP methods available)
        return mfaInitialized;
      }

      // No MFA required - create session directly
      const newSession = new EnduserSession({
        host: getApiURL(),
        authToken: result.authToken,
        businessId: result.enduser?.businessId || "",
        handleUnauthenticated: async () => {
          get().clearAuth();
        },
      });

      set({
        session: newSession,
        currentEnduser: result.enduser,
        enduserPasswordIsUnset: false, // User just authenticated with a password
        isLoading: false,
        tokenError: null,
        // Clear login flow state
        loginFlowEmail: null,
        loginFlowOtpToken: null,
        loginFlowError: null,
        loginFlowPasswordIsUnset: false,
      });

      return true;
    } catch (error) {
      console.error("Password authentication failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Invalid email or password";
      set({ loginFlowError: errorMessage, isLoading: false });
      return false;
    }
  },

  verifyLoginFlowOtp: async (code: string): Promise<boolean> => {
    const { loginFlowOtpToken } = get();
    if (!loginFlowOtpToken) {
      set({ loginFlowError: "No OTP token available" });
      return false;
    }

    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";

      // Create a temporary session to verify OTP
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: loginFlowOtpToken,
        businessId,
      });

      const result = await tempSession.api.endusers.verify_otp({
        token: loginFlowOtpToken,
        code,
      });

      // Create a new session with the authenticated token
      const newSession = new EnduserSession({
        host: getApiURL(),
        authToken: result.authToken,
        businessId: result.enduser?.businessId || "",
        handleUnauthenticated: async () => {
          get().clearAuth();
        },
      });

      const dontPromptSetPassword =
        useOrganizationStore.getState().organization?.portalSettings?.authentication?.dontPromptSetPassword ?? false;

      set({
        session: newSession,
        currentEnduser: result.enduser,
        enduserPasswordIsUnset: dontPromptSetPassword ? false : get().loginFlowPasswordIsUnset,
        isLoading: false,
        tokenError: null,
        // Clear login flow state
        loginFlowEmail: null,
        loginFlowOtpToken: null,
        loginFlowError: null,
        loginFlowPasswordIsUnset: false,
      });

      return true;
    } catch (error) {
      console.error("Login OTP verification failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Invalid code";
      set({ loginFlowError: errorMessage, isLoading: false });
      return false;
    }
  },

  resendLoginFlowOtp: async (): Promise<boolean> => {
    const { loginFlowEmail } = get();
    if (!loginFlowEmail) {
      set({ loginFlowError: "No email/phone available for resend" });
      return false;
    }

    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";
      const brandId = organization?.brandId;

      // Create a temporary session for the login flow
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: "",
        businessId,
      });

      // Re-initiate the login flow to get a new OTP sent
      const isEmail = loginFlowEmail.includes("@");
      const loginArg = isEmail
        ? { email: loginFlowEmail, ...(brandId && { brandId }) }
        : { phone: loginFlowEmail, ...(brandId && { brandId }) };

      const result = await tempSession.begin_login_flow(loginArg);

      // Update the OTP token
      set({
        loginFlowOtpToken: result.otpToken || null,
        isLoading: false,
      });

      return true;
    } catch (error) {
      console.error("Resend login OTP failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to resend code";
      set({ loginFlowError: errorMessage, isLoading: false });
      return false;
    }
  },

  // Registration

  register: async (args: RegisterArgs): Promise<boolean> => {
    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";

      // Create a temporary session for registration
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: "",
        businessId,
      });

      await tempSession.register({
        email: args.email,
        password: args.password,
        fname: args.fname,
        lname: args.lname,
        emailConsent: true,
      });

      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error("Registration failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Registration failed";
      set({ loginFlowError: errorMessage, isLoading: false });
      return false;
    }
  },

  // Password Reset

  requestPasswordReset: async (email: string): Promise<boolean> => {
    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";

      // Create a temporary session
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: "",
        businessId,
      });

      await tempSession.request_password_reset({ email, businessId });

      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error("Password reset request failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send reset email";
      set({ loginFlowError: errorMessage, isLoading: false });
      return false;
    }
  },

  resetPassword: async (resetToken: string, newPassword: string): Promise<boolean> => {
    set({ loginFlowError: null, isLoading: true });

    try {
      const organization = useOrganizationStore.getState().organization;
      const businessId = organization?.businessId || "";

      // Create a temporary session
      const tempSession = new EnduserSession({
        host: getApiURL(),
        authToken: "",
        businessId,
      });

      await tempSession.reset_password({ resetToken, newPassword, businessId });

      set({ isLoading: false });
      return true;
    } catch (error) {
      console.error("Password reset failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to reset password";
      set({ loginFlowError: errorMessage, isLoading: false });
      return false;
    }
  },

  setPassword: async (password: string): Promise<boolean> => {
    const { session, currentEnduser } = get();
    if (!session || !currentEnduser) return false;

    set({ isLoading: true });
    try {
      await session.api.endusers.set_password({ id: currentEnduser.id, password });
      set({ isLoading: false, enduserPasswordIsUnset: false });
      return true;
    } catch (error) {
      console.error("Failed to set password:", error);
      set({ isLoading: false });
      return false;
    }
  },

  dismissPasswordPrompt: () => set({ passwordPromptDismissed: true }),

  // Terms of Service

  acceptTerms: async (): Promise<boolean> => {
    const { session, currentEnduser } = get();
    if (!session || !currentEnduser) return false;

    const organization = useOrganizationStore.getState().organization;
    const termsVersion = organization?.customPoliciesVersion || "v1";

    try {
      await session.api.endusers.updateOne(currentEnduser.id, {
        termsSigned: new Date(),
        termsVersion,
      });

      // Update local state
      set({
        currentEnduser: {
          ...currentEnduser,
          termsSigned: new Date(),
          termsVersion,
        },
      });

      return true;
    } catch (error) {
      console.error("Failed to accept terms:", error);
      return false;
    }
  },

  reset: () => {
    get().clearAuth();
  },
});
